import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MoreHorizontal, Plus, Trash2 } from 'lucide-react'
import type { KeyboardEvent } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/common/Toast'
import {
  useCreatePicklistEntry,
  useDeletePicklistEntry,
  usePicklistByKind,
  type PicklistEntry,
  type PicklistKind,
} from '@/modules/picklist/services/picklist'
import { cn } from '@/lib/utils'

type ManagedPicklistComboboxProps = {
  kind: PicklistKind
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  gridRow?: number
  gridCol?: number
  onGridKeyDown?: (event: KeyboardEvent<HTMLElement>, row: number, col: number) => void
}

export function ManagedPicklistCombobox({
  kind,
  value,
  onChange,
  placeholder = 'พิมพ์หรือเลือกรายการ',
  disabled,
  className,
  gridRow,
  gridCol,
  onGridKeyDown,
}: ManagedPicklistComboboxProps) {
  const { data, isLoading } = usePicklistByKind(kind)
  const entries = data?.data ?? []
  const createMut = useCreatePicklistEntry()
  const deleteMut = useDeletePicklistEntry()

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState(value)
  const [addOpen, setAddOpen] = useState(false)
  const [manageOpen, setManageOpen] = useState(false)
  const [newLabel, setNewLabel] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<PicklistEntry | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const DROPDOWN_GAP = 4
  const DROPDOWN_MAX_HEIGHT = 192

  const [dropdownRect, setDropdownRect] = useState<{
    top: number
    left: number
    width: number
    maxHeight: number
    placement: 'above' | 'below'
  } | null>(null)

  const updateDropdownRect = useCallback(() => {
    const el = inputRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP
    const spaceAbove = rect.top - DROPDOWN_GAP
    const openBelow = spaceBelow >= 96 || spaceBelow >= spaceAbove
    const maxHeight = Math.max(
      80,
      Math.min(DROPDOWN_MAX_HEIGHT, (openBelow ? spaceBelow : spaceAbove) - DROPDOWN_GAP),
    )

    setDropdownRect({
      top: openBelow ? rect.bottom + DROPDOWN_GAP : rect.top - DROPDOWN_GAP,
      left: rect.left,
      width: rect.width,
      maxHeight,
      placement: openBelow ? 'below' : 'above',
    })
  }, [])

  useEffect(() => {
    setSearch(value)
  }, [value])

  useEffect(() => {
    if (!open) {
      setDropdownRect(null)
      return
    }
    updateDropdownRect()
    window.addEventListener('scroll', updateDropdownRect, true)
    window.addEventListener('resize', updateDropdownRect)
    return () => {
      window.removeEventListener('scroll', updateDropdownRect, true)
      window.removeEventListener('resize', updateDropdownRect)
    }
  }, [open, updateDropdownRect])

  const labels = useMemo(() => entries.map((entry) => entry.label), [entries])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return labels
    return labels.filter((label) => label.toLowerCase().includes(q))
  }, [labels, search])

  const qTrim = search.trim()
  const showUseCustom =
    qTrim.length > 0 && !filtered.some((label) => label.toLowerCase() === qTrim.toLowerCase())

  const selectValue = (next: string) => {
    onChange(next)
    setSearch(next)
    setOpen(false)
  }

  const handleAdd = async () => {
    const label = newLabel.trim()
    if (!label) {
      toast.toastFailed('กรุณากรอกชื่อรายการ')
      return
    }
    try {
      await createMut.mutateAsync({ kind, label })
      onChange(label)
      setSearch(label)
      setNewLabel('')
      setAddOpen(false)
      toast.toastSuccess('เพิ่มรายการแล้ว')
    } catch (e) {
      toast.toastFailed(e instanceof Error ? e.message : 'เพิ่มไม่สำเร็จ')
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMut.mutateAsync({ id: deleteTarget.id })
      if (deleteTarget.label === value.trim()) onChange('')
      setDeleteTarget(null)
      toast.toastSuccess('ลบรายการแล้ว')
    } catch (e) {
      toast.toastFailed(e instanceof Error ? e.message : 'ลบไม่สำเร็จ')
    }
  }

  const showDropdown =
    open && !disabled && (isLoading || filtered.length > 0 || showUseCustom || qTrim.length > 0)

  return (
    <>
      <div className="flex min-w-0 items-stretch gap-1">
        <div className="relative min-w-0 flex-1">
          <Input
            ref={inputRef}
            className={cn('h-9 w-full min-w-[7rem] border-border/80 bg-background px-2 text-sm', className)}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            value={search}
            data-grid-row={gridRow}
            data-grid-col={gridCol}
            onChange={(e) => {
              const next = e.target.value
              setSearch(next)
              onChange(next)
              setOpen(true)
            }}
            onFocus={() => {
              setOpen(true)
              updateDropdownRect()
            }}
            onBlur={() => {
              window.setTimeout(() => setOpen(false), 150)
            }}
            onKeyDown={(event) => {
              if (gridRow != null && gridCol != null) {
                onGridKeyDown?.(event, gridRow, gridCol)
              }
            }}
          />
          {showDropdown && dropdownRect
            ? createPortal(
                <div
                  className="fixed z-[300] overflow-y-auto rounded-md border border-border/80 bg-popover p-1 text-popover-foreground shadow-md"
                  style={{
                    top: dropdownRect.top,
                    left: dropdownRect.left,
                    width: dropdownRect.width,
                    maxHeight: dropdownRect.maxHeight,
                    transform:
                      dropdownRect.placement === 'above' ? 'translateY(-100%)' : undefined,
                  }}
                  role="listbox"
                >
                  {isLoading ? (
                    <p className="px-2 py-2 text-center text-xs text-muted-foreground">กำลังโหลด...</p>
                  ) : null}
                  {filtered.map((label) => (
                    <button
                      key={label}
                      type="button"
                      className={cn(
                        'flex w-full cursor-pointer rounded-sm px-2 py-1.5 text-left text-sm outline-none',
                        'hover:bg-accent hover:text-accent-foreground',
                        value === label && 'bg-accent text-accent-foreground',
                      )}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectValue(label)}
                    >
                      {label}
                    </button>
                  ))}
                  {showUseCustom ? (
                    <button
                      type="button"
                      className="flex w-full cursor-pointer rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectValue(qTrim)}
                    >
                      ใช้ &quot;{qTrim}&quot;
                    </button>
                  ) : null}
                </div>,
                document.body,
              )
            : null}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              disabled={disabled}
              aria-label="เมนูจัดการรายการ"
              tabIndex={-1}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[12rem]">
            <DropdownMenuItem className="text-sm py-2" onSelect={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              เพิ่มรายการ…
            </DropdownMenuItem>
            <DropdownMenuItem className="text-sm py-2" onSelect={() => setManageOpen(true)}>
              จัดการรายการ…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">เพิ่มรายการใหม่</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Input
              className="min-h-11 text-base"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="ชื่อที่แสดงในรายการ"
              maxLength={255}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void handleAdd()
                }
              }}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
              ยกเลิก
            </Button>
            <Button type="button" disabled={createMut.isPending} onClick={() => void handleAdd()}>
              บันทึก
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg">จัดการรายการ</DialogTitle>
          </DialogHeader>
          <ul className="space-y-2 py-2">
            {entries.length === 0 ? (
              <li className="text-sm text-muted-foreground">ยังไม่มีรายการ</li>
            ) : (
              entries.map((entry) => (
                <li
                  key={entry.id}
                  className={cn(
                    'flex items-center justify-between gap-2 rounded-lg border px-3 py-2',
                    entry.label === value.trim() && 'border-s-brand-primary bg-secondary/60',
                  )}
                >
                  <span className="text-sm font-medium">{entry.label}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => setDeleteTarget(entry)}
                    aria-label={`ลบ ${entry.label}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))
            )}
          </ul>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg">ลบรายการ?</AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              ลบ &quot;{deleteTarget?.label}&quot; ออกจากรายการเลือก (บิลเก่าที่ใช้ชื่อนี้ยังคงแสดงได้)
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(ev) => {
                ev.preventDefault()
                void confirmDelete()
              }}
            >
              ลบ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
